import Service, { inject as service } from '@ember/service';

import type ApiService from 'api/services/api';
import type AbilitiesService from 'ember-can/services/abilities';

import type { Organization, Project } from 'core/services/user-context';
import type RolesLocationsService from 'manage-access/services/roles-locations';
import type ScopesService from 'manage-access/services/scopes';

import type { EmberAdapterError } from '../types/ember-adapter-error.ts';
import type { IResourceContext } from '../types/role-assignments-config.ts';
import type { RolesLocation } from '../types/roles-location.ts';
import type { ScopeLocationLink } from '../types/scope-location-link.ts';

export default class RoleAssignmentsSetupService extends Service {
  @service declare readonly api: ApiService;
  @service declare readonly abilities: AbilitiesService;
  @service declare readonly rolesLocations: RolesLocationsService;
  @service declare readonly scopes: ScopesService;

  async fetch({
    organization,
    project,
    resourceContext,
  }: {
    organization?: Organization;
    project?: Project;
    resourceContext?: IResourceContext;
  }): Promise<{
    currentRolesLocation: RolesLocation;
    currentScopeLink: ScopeLocationLink;
    organizationId: string | undefined;
    rolesLocations: Array<RolesLocation>;
    scopeLinks: Array<ScopeLocationLink>;
  }> {
    const [{ currentScopeLink, scopeLinks }, rolesLocations] =
      await Promise.all([
        this.scopes.fetch({
          organization,
          project,
          resource: resourceContext,
        }),
        this.rolesLocations.fetch({
          organization,
          project,
          resource: resourceContext,
        }),
      ]);

    const currentRolesLocation = rolesLocations.find((rolesLocation) => {
      return rolesLocation.link.id === currentScopeLink?.id;
    });

    let organizationId;

    if (organization && organization.id) {
      organizationId = organization.id;
    }

    if (project && project?.parent?.id && !organizationId) {
      organizationId = project?.parent?.id;
    }

    if (!currentRolesLocation) {
      const error: EmberAdapterError = new Error('InvalidError');
      error.name = 'Role assignments require a currentRolesLocation';
      error.status = 400;
      throw error;
    }

    if (!currentScopeLink) {
      const error: EmberAdapterError = new Error('InvalidError');
      error.name = 'Role assignments require a currentScopeLink';
      error.status = 400;
      throw error;
    }

    if (!rolesLocations) {
      const error: EmberAdapterError = new Error('InvalidError');
      error.name = 'Role assignments require a rolesLocations';
      error.status = 400;
      throw error;
    }

    return {
      currentRolesLocation,
      currentScopeLink,
      organizationId,
      rolesLocations,
      scopeLinks,
    };
  }
}
